import { Stack, useTheme } from '@ltvco/refresh-lib/theme';
import { isWithinLast12Months } from '@ltvco/refresh-lib/utils';
import {
  AiChip,
  type NavLinkData,
  type serializedTypes,
  type SerializedPropertyReport,
  type propertiesTypes,
} from '@ltvco/refresh-lib/v1';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import {
  Home,
  LocationOn,
  Gavel,
  TaskAlt,
  Group,
  Circle,
  MonetizationOn,
  School,
  AssignmentSharp,
  Analytics,
  PlaylistAddCheck,
  PinDrop,
  HolidayVillage,
  SsidChart,
} from '@mui/icons-material';

import { PROPERTY_REPORT_SECTION_ORDER_DEFAULT } from 'components/reports/propertyReportSections/propertyReportSectionOrder.constants';
import { getPropertyDetailsCount } from 'utils/propertyReportUtils';

import { sortNavItemsBySectionOrder } from './sortNavItemsBySectionOrder';

import type { PropertySectionKey } from 'components/reports/propertyReportSections/propertySectionKeys';

interface PropertyNavLinkData extends NavLinkData {
  visible: boolean;
}
export const getPropertyNavLinkData = (
  potentialOwnersLength: number,
  possibleResidentsLength: number,
  buildingPermitsLength: number,
  deedsLength: number,
  liensLength: number,
  propertyDetailsLength: number,
  propertyValuesLength: number,
  equitiesCount: number,
  schoolsCount: number
) => {
  const isInPropertySmartInsightsVariation = useFeatureIsOn('prop-2296');

  return [
    {
      href: '#overview',
      sectionId: '#overview',
      text: 'Overview',
      reportItemsCount: null,
      hideReportItemsCount: true,
      notificationSection: 'identity',
      name: 'overview',
    },
    {
      href: '#property-professional-insights',
      sectionId: '#property-professional-insights',
      text: 'Insights for Professionals',
      visible: true,
      reportItemsCount: 0,
      notificationSection: 'property professional insights',
      icon: <AiChip />,
      showIcon: true,
      name: 'property-professional-insights',
    },
    (isInPropertySmartInsightsVariation && {
      href: '#property-smart-insights',
      sectionId: '#property-smart-insights',
      text: 'Property Smart Insights',
      visible: true,
      reportItemsCount: 0,
      notificationSection: 'property smart insights',
      icon: <AiChip />,
      showIcon: true,
      name: 'property-smart-insights',
    }) as PropertyNavLinkData,
    {
      href: '#possible-owners-section',
      sectionId: '#possible-owners-section',
      text: 'Possible Owners',
      reportItemsCount: potentialOwnersLength,
      notificationSection: 'owners',
      name: 'possible-owners',
    },
    {
      href: '#possible-residents-section',
      sectionId: '#possible-residents-section',
      text: 'Possible Residents',
      reportItemsCount: possibleResidentsLength,
      notificationSection: 'residents',
      name: 'possible-residents',
    },
    {
      href: '#lot-and-building-details-section',
      sectionId: '#lot-and-building-details-section',
      text: 'Property Details',
      reportItemsCount: propertyDetailsLength,
      notificationSection: 'lot_details',
      notificationSectionMapping: [
        { text: 'lot_details', href: '#lot-and-building-details-section' },
        { text: 'values', href: '#assessed-value-and-taxes-section' },
      ],
      name: 'property-details',
    },
    {
      href: '#building-permits-section',
      sectionId: '#building-permits-section',
      text: 'Building Permits',
      reportItemsCount: buildingPermitsLength,
      notificationSection: 'permits',
      name: 'building-permits',
    },
    {
      href: '#deeds-section',
      sectionId: '#deeds-section',
      text: 'Deeds',
      reportItemsCount: deedsLength,
      notificationSection: 'deeds',
      name: 'deeds',
    },
    {
      href: '#liens-section',
      sectionId: '#liens-section',
      text: 'Liens',
      reportItemsCount: liensLength,
      notificationSection: 'liens',
      name: 'liens',
    },
    {
      href: '#estimated-value-section',
      sectionId: '#estimated-value-section',
      text: 'Property Valuation',
      reportItemsCount: propertyValuesLength,
      notificationSection: 'valuation',
      name: 'property-valuation',
    },
    {
      href: '#equity-and-loans-section',
      sectionId: '#equity-and-loans-section',
      text: 'Equity & Loans',
      reportItemsCount: equitiesCount,
      notificationSection: 'equity_loans',
      name: 'equity-&-loans',
    },
    {
      href: '#schools-nearby-section',
      sectionId: '#schools-nearby-section',
      text: 'Schools & Area',
      reportItemsCount: schoolsCount,
      notificationSection: 'schools',
      notificationSectionMapping: [
        { text: 'schools', href: '#schools-nearby-section' },
        { text: 'parcel_address', href: '#demographics-section' },
      ],
      name: 'schools-&-area',
    },
    {
      href: '#rewards-section',
      sectionId: '#rewards-section',
      text: 'Member Perks',
      reportItemsCount: null,
      hideReportItemsCount: true,
      notificationSection: 'memberPerks',
      name: 'member-perks',
    },
  ].filter(Boolean) as PropertyNavLinkData[];
};

// V2 Property Report Navigation Menu
/**
 * Builds the V2 property-report sidebar. Pass `sectionOrder` so group and
 * child order match the report body (UPS-1263). Defaults to the homebuyer
 * order, which is the current hardcoded nav.
 */
export const getPropertyNavLinkDataV2 = (
  reportData: SerializedPropertyReport,
  marketSortedBySource: propertiesTypes.MarketValues[],
  sectionOrder: readonly PropertySectionKey[] = PROPERTY_REPORT_SECTION_ORDER_DEFAULT
) => {
  const property: serializedTypes.SerializedProperty = reportData.properties[0];
  const theme = useTheme();

  // People
  const owners = property.owners.length ?? 0;
  const residents = property.residents.length ?? 0;

  // Property
  const propertyDetailsCount = getPropertyDetailsCount(property);
  const building_permits = property.building_permits.length ?? 0;
  const hoas = property.hoas.length ?? 0;
  const taxes =
    property.taxes?.bills?.filter(
      (bill) => Boolean(bill.amount) && Boolean(bill.year)
    ).length ?? 0;
  const equitiesCount = property.equities.length ?? 0;
  const marketValuesCount = marketSortedBySource.reduce((count, value) => {
    const hasValidItem = value.some((val) =>
      isWithinLast12Months(new Date(val.date?.full ?? ''))
    );
    return count + (hasValidItem ? 1 : 0);
  }, 0);
  const propertyValuesCount = equitiesCount + marketValuesCount;

  // Legal & Recorded Docs
  const deeds = property.deeds.length ?? 0;
  const liens = property.courts?.civils?.length || 0;

  // Market & Area Insights
  const demographicsCount = 1;
  const schools = reportData.schools.length ?? 0;
  const sexOffendersCount = reportData.sex_offenders?.length ?? 0;

  const menuItems = [
    {
      href: '#property-overview',
      labelText: 'Overview',
      name: 'overview',
      icons: [
        <Stack position="relative" alignItems="center" justifyContent="center">
          <Circle
            sx={{ color: theme.palette.primary.main, fontSize: '2.5rem' }}
          />
          <Home
            sx={{
              color: theme.palette.primary.contrastText,
              fontSize: '1.5rem',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1,
            }}
          />
        </Stack>,
      ],
      notificationSection: 'identity',
    },
    {
      labelText: 'Property',
      name: 'property',
      icons: [
        <Home
          key="property-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.25rem' }}
        />,
      ],
      childItems: [
        {
          labelText: 'Property Details',
          name: 'property-details',
          href: '#lot-and-building-details-section',
          reportItemsCount: propertyDetailsCount,
          notificationSection: 'lot_details',
        },
        {
          labelText: 'Taxes',
          name: 'taxes',
          href: '#assessed-value-and-taxes-section',
          reportItemsCount: taxes,
          notificationSection: 'taxes',
        },
        {
          labelText: 'Know Your Neighborhood',
          name: 'know-your-neighborhood',
          href: '#know-your-neighborhood-section',
          hideReportItemsCount: true,
          notificationSection: 'know_your_neighborhood',
        },
        {
          labelText: 'Building Permits',
          name: 'building-permits',
          href: '#building-permits-section',
          reportItemsCount: building_permits,
          notificationSection: 'permits',
        },
        {
          labelText: 'HOA',
          name: 'hoa',
          href: '#homeowner-association-section',
          reportItemsCount: hoas,
          notificationSection: 'hoa',
        },
      ],
    },
    {
      labelText: 'Valuation & Loans',
      name: 'valuation-&-loans',
      icons: [
        <MonetizationOn
          key="valuation-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.25rem' }}
        />,
      ],
      childItems: [
        {
          labelText: 'Valuation History',
          name: 'valuation-history',
          href: '#property-valuation-section',
          reportItemsCount: propertyValuesCount,
          notificationSection: 'valuation',
        },
        {
          labelText: 'Equity & Loans',
          name: 'equity-&-loans',
          href: '#equity-and-loans-section',
          reportItemsCount: equitiesCount,
          notificationSection: 'equity_loans',
        },
      ],
    },
    {
      labelText: 'People',
      name: 'people',
      icons: [
        <Group
          key="people-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.25rem' }}
        />,
      ],
      childItems: [
        {
          labelText: 'Possible Owners',
          name: 'possible-owners',
          href: '#possible-owners-section',
          reportItemsCount: owners,
          notificationSection: 'owners',
        },
        {
          labelText: 'Possible Residents',
          name: 'possible-residents',
          href: '#possible-residents-section',
          reportItemsCount: residents,
          notificationSection: 'residents',
        },
      ],
    },
    {
      labelText: 'Legal & Deeds',
      name: 'legal-&-deeds',
      icons: [
        <Gavel
          key="legal-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.25rem' }}
        />,
      ],
      childItems: [
        {
          labelText: 'Deeds',
          name: 'deeds',
          href: '#deeds-section',
          reportItemsCount: deeds,
          notificationSection: 'deeds',
        },
        {
          labelText: 'Liens',
          name: 'liens',
          href: '#liens-section',
          reportItemsCount: liens,
          notificationSection: 'liens',
        },
      ],
    },
    {
      labelText: 'Local Market Insights',
      name: 'local-market-insights',
      icons: [
        <LocationOn
          key="market-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.25rem' }}
        />,
      ],
      childItems: [
        {
          labelText: 'Market Insights',
          name: 'market-insights',
          href: '#market-insights',
          hideReportItemsCount: true,
          notificationSection: 'market_insights',
        },
        {
          labelText: 'Crime Incidents',
          name: 'crime-incidents',
          href: '#crime-incidents-section',
          hideReportItemsCount: true,
          notificationSection: 'crime_incidents',
        },
        {
          labelText: 'Sex Offenders',
          name: 'sex-offenders',
          href: '#sex-offenders-section',
          reportItemsCount: sexOffendersCount,
          notificationSection: 'sex_offenders',
        },
        {
          labelText: 'Demographics',
          name: 'demographics',
          href: '#demographics-section',
          reportItemsCount: demographicsCount,
          notificationSection: 'demographics',
        },
        {
          labelText: 'Schools',
          name: 'schools',
          href: '#schools-nearby-section',
          reportItemsCount: schools,
          notificationSection: 'schools',
        },
        {
          labelText: 'Natural Hazards',
          name: 'natural-hazards',
          href: '#natural-hazards-section',
          hideReportItemsCount: true,
          notificationSection: 'natural_hazards',
        },
      ],
    },
    {
      labelText: 'Next Steps',
      name: 'next-steps',
      icons: [
        <TaskAlt
          key="next-steps-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.25rem' }}
        />,
      ],
      childItems: [
        {
          labelText: 'Member Perks',
          name: 'member-perks',
          href: '#rewards-section',
          hideReportItemsCount: true,
          notificationSection: 'memberPerks',
        },
        {
          href: '#attachments-section',
          labelText: 'Report Attachments',
          hideReportItemsCount: true,
          name: 'attachments',
          visible: false,
        },
        {
          labelText: 'Notes',
          name: 'notes',
          href: '#Notes-section',
          hideReportItemsCount: true,
          notificationSection: 'notes',
        },
        {
          href: '#related-reports-section',
          labelText: 'Related Reports',
          hideReportItemsCount: true,
          name: 'related-reports',
        },
        {
          labelText: 'Report Rating',
          name: 'report-rating',
          href: '#report-rating',
          hideReportItemsCount: true,
          notificationSection: 'report_rating',
        },
      ],
    },
  ];

  return sortNavItemsBySectionOrder(menuItems, sectionOrder);
};

export const getPropertyNeighborhoodNavLinkData = () => {
  const theme = useTheme();

  return [
    {
      href: '#property-report-tabpanel-neighborhood',
      labelText: 'Overview',
      name: 'overview',
      icons: [
        <Stack position="relative" alignItems="center" justifyContent="center">
          <Circle
            sx={{ color: theme.palette.primary.main, fontSize: '2.5rem' }}
          />
          <Home
            sx={{
              color: theme.palette.primary.contrastText,
              fontSize: '1.5rem',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1,
            }}
          />
        </Stack>,
      ],
      notificationSection: 'identity',
    },
    {
      labelText: 'Market trends',
      name: 'market-trends',
      icons: [
        <SsidChart
          key="market-trends-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.25rem' }}
        />,
      ],
      childItems: [
        {
          labelText: 'Housing market conditions',
          name: 'housing-market-conditions',
          href: '#neighborhood-market-trends-housing-market-conditions-section',
          hideReportItemsCount: true,
          notificationSection: 'neighborhood_market_trends_housing_conditions',
        },
        {
          labelText: 'Recent Trends',
          name: 'recent-trends',
          href: '#neighborhood-market-trends-recent-trends-section',
          hideReportItemsCount: true,
          notificationSection: 'neighborhood_market_trends_recent',
        },
        {
          labelText: 'Market Current Activity',
          name: 'market-current-activity',
          href: '#neighborhood-market-trends-current-activity-section',
          hideReportItemsCount: true,
          notificationSection: 'neighborhood_market_trends_current_activity',
        },
      ],
    },
    {
      labelText: 'Characteristics',
      name: 'characteristics',
      icons: [
        <AssignmentSharp
          key="characteristics-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.25rem' }}
        />,
      ],
      childItems: [
        {
          labelText: 'Transportation',
          name: 'transportation',
          href: '#neighborhood-characteristics-transportation-section',
          hideReportItemsCount: true,
          notificationSection: 'neighborhood_characteristics_transportation',
        },
        {
          labelText: 'Services',
          name: 'services',
          href: '#neighborhood-characteristics-services-section',
          hideReportItemsCount: true,
          notificationSection: 'neighborhood_characteristics_services',
        },
        {
          labelText: 'Character',
          name: 'character',
          href: '#neighborhood-characteristics-character-section',
          hideReportItemsCount: true,
          notificationSection: 'neighborhood_characteristics_character',
        },
      ],
    },
    {
      labelText: 'Demographics',
      name: 'demographics',
      icons: [
        <Analytics
          key="demographics-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.25rem' }}
        />,
      ],
      childItems: [
        {
          labelText: 'Income',
          name: 'income',
          href: '#neighborhood-demographics-income-section',
          hideReportItemsCount: true,
          notificationSection: 'neighborhood_demographics_income',
        },
        {
          labelText: 'Population',
          name: 'population',
          href: '#neighborhood-demographics-population-section',
          hideReportItemsCount: true,
          notificationSection: 'neighborhood_demographics_population',
        },
        {
          labelText: 'Housing',
          name: 'housing',
          href: '#neighborhood-demographics-housing-section',
          hideReportItemsCount: true,
          notificationSection: 'neighborhood_demographics_housing',
        },
        {
          labelText: 'Households',
          name: 'households',
          href: '#neighborhood-demographics-households-section',
          hideReportItemsCount: true,
          notificationSection: 'neighborhood_demographics_households',
        },
        {
          labelText: 'Education',
          name: 'education',
          href: '#neighborhood-demographics-education-section',
          hideReportItemsCount: true,
          notificationSection: 'neighborhood_demographics_education',
        },
        {
          labelText: 'Commute',
          name: 'commute',
          href: '#neighborhood-demographics-commute-section',
          hideReportItemsCount: true,
          notificationSection: 'neighborhood_demographics_commute',
        },
      ],
    },
    {
      labelText: 'Property Value Drivers',
      name: 'property-value-drivers',
      icons: [
        <PlaylistAddCheck
          key="property-value-drivers-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.25rem' }}
        />,
      ],
      href: '#neighborhood-property-value-drivers-section',
      hideReportItemsCount: true,
      notificationSection: 'neighborhood_property_value_drivers',
    },
    {
      labelText: 'Neighborhood Map Insights',
      name: 'neighborhood-map-insights',
      icons: [
        <PinDrop
          key="neighborhood-map-insights-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.25rem' }}
        />,
      ],
      childItems: [
        {
          labelText: 'Points of Interest',
          name: 'points-of-interest',
          href: '#neighborhood-map-insights-points-of-interest-section',
          hideReportItemsCount: true,
          notificationSection: 'neighborhood_map_poi',
        },
        {
          labelText: 'Crime Incidents',
          name: 'crime-incidents',
          href: '#neighborhood-map-insights-crime-incidents-section',
          hideReportItemsCount: true,
          notificationSection: 'neighborhood_map_crime',
        },
      ],
    },
    {
      labelText: 'Nearby schools',
      name: 'nearby-schools',
      icons: [
        <School
          key="nearby-schools-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.25rem' }}
        />,
      ],
      href: '#neighborhood-nearby-schools-section',
      hideReportItemsCount: true,
      notificationSection: 'neighborhood_nearby_schools',
    },
    {
      labelText: 'Similar Neighborhoods',
      name: 'similar-neighborhoods',
      icons: [
        <HolidayVillage
          key="similar-neighborhoods-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.25rem' }}
        />,
      ],
      href: '#neighborhood-similar-neighborhoods-section',
      hideReportItemsCount: true,
      notificationSection: 'neighborhood_similar_neighborhoods',
    },
  ];
};
